import { Button } from 'components/Phantom/Button'
import styles from './QuizModal.module.scss'
import { Type } from 'components/Type'
import React from 'react'
import { Icon } from 'components/Phantom/Icon'
import { Logo } from 'components/Logo'
import cx from 'classnames'
import {
	AllQuizAnswers,
	getSleepAnalysis,
	QuizInputStates,
	SleepScoreAnalysis,
} from './QuizQuestions'
import { Img } from 'components/basic/Img'

type Persona = {
	name: string
	personalizedTitle: string
	explanation: string
	bottomTitle: string
	bottomSubtitle: string
	answerIDs: string[]
	minMatchNumber: number
	benefits: {
		icon: string
		text: React.ReactNode
	}[]
}

// if no persona matches, use this persona
const catchAllPersona: Persona = {
	name: 'Temperature Sensitive Sleeper',
	personalizedTitle: 'you are a temperature sensitive sleeper',
	explanation:
		'The right climate is key to your sleep. Whether cool or warm,  the perfect temperature helps you rest deeply and wake up refreshed.',
	bottomTitle: 'Regulate your temperature',
	bottomSubtitle:
		'The Pod optimizes your bed’s temperature to help you fall asleep faster and stay asleep longer.',
	answerIDs: [],
	minMatchNumber: 0,
	benefits: [
		{
			icon: 'TemperatureLight',
			text: (
				<span>
					<span className={styles.benefits_text_bold}>
						Pre-setting your bed temperature and automatically adjusting it in real-time
					</span>{' '}
					based on your unique biometric data.
				</span>
			),
		},
	],
}

// Maps the quiz answers to a persona
const personaMappings: Persona[] = [
	{
		name: 'Sleep Optimizer',
		personalizedTitle: 'you are a sleep optimizer',
		explanation:
			'Better recovery means better performance — focus on getting high-quality, restorative sleep.',
		bottomTitle: 'Maximize your recovery',
		bottomSubtitle:
			'The Pod optimizes your bed temperature so you sleep deeper and fall asleep faster.',
		answerIDs: [
			'sleepPatternWithoutInterruption',
			'sleepDuration7To8Hours',
			'sleepDurationMoreThan9Hours',
			'timeToFallAsleepLessThan10Minutes',
			'sleepQualityRefreshed',
			'sleepMetricsYesVeryInterested',
		],
		minMatchNumber: 4,
		benefits: [
			{
				icon: 'TemperatureLight',
				text: (
					<span>
						<span className={styles.benefits_text_bold}>
							Automatically adjusting your temperature in real-time
						</span>{' '}
						based on your biometrics, giving your body what it needs to regenerate.
					</span>
				),
			},
		],
	},
	{
		name: 'Restless Sleeper',
		personalizedTitle: 'you are a restless sleeper',
		explanation:
			'Waking up at night disrupts deep sleep, leaving you drained. A stable sleep environment helps you rest longer and wake up refreshed.',
		bottomTitle: 'Combat restless nights',
		bottomSubtitle:
			'The Pod optimizes temperature and body position for deeper, more restorative sleep.',
		answerIDs: [
			'sleepPatternWakeUp',
			'sleepDurationLessThan7Hours',
			'timeToFallAsleepLessThan10Minutes',
			'sleepQualityGroggy',
		],
		minMatchNumber: 3,
		benefits: [
			{
				icon: 'TemperatureLight',
				text: (
					<span>
						<span className={styles.benefits_text_bold}>
							Keeping your body at the optimal temperature and position
						</span>{' '}
						for deep, restorative sleep all night long.
					</span>
				),
			},
		],
	},
	{
		name: 'Sleep Struggler',
		personalizedTitle: 'you are a disrupted sleeper',
		explanation: `Restless nights and frequent wake-ups leave you exhausted. It's time for a sleep setup that helps you stay asleep and wake up refreshed.`,
		bottomTitle: 'Combat restless nights',
		bottomSubtitle:
			'The Pod optimizes your bed’s temperature to help you fall asleep faster and stay asleep longer.',
		answerIDs: [
			'sleepPatternStruggleToStayAsleep',
			'sleepDurationLessThan7Hours',
			'timeToFallAsleepMoreThan30Minutes',
			'sleepQualityExhausted',
		],
		minMatchNumber: 3,
		benefits: [
			{
				icon: 'TemperatureLight',
				text: (
					<span>
						<span className={styles.benefits_text_bold}>
							Automatically adjusting your temperature in real-time
						</span>{' '}
						to keep you in the optimal range for deep, restorative sleep.
					</span>
				),
			},
		],
	},
	{
		name: 'Sleep Tracker',
		personalizedTitle: 'you are a data-seeking sleeper',
		explanation:
			'Based on your answers, you want to break the cycle of restless nights. Getting enough restorative sleep is the most foundational element to improving your recovery.',
		bottomTitle: 'Track your data without a wearable',
		bottomSubtitle:
			'The Pod tracks your sleep  and health performance, so you can optimize rest without the hassle of wearables.',
		answerIDs: ['sleepMetricsYesVeryInterested'],
		minMatchNumber: 1,
		benefits: [
			{
				icon: 'PrimeDark',
				text: (
					<span>
						<span className={styles.benefits_text_bold}>Using clinical-grade sensors</span> to
						detect your current sleep stage and biometrics.
					</span>
				),
			},
			{
				icon: 'AdjustmentsDark',
				text: (
					<span>
						<span className={styles.benefits_text_bold}>Using AI</span> to adjust your temperature
						based on your sleep stage, increasing deep and REM sleep.
					</span>
				),
			},
		],
	},
]

const getSleepSnapshotRows = (
	quizAnswers: AllQuizAnswers
): {
	icon: string
	label: string
	className?: string
	analysis?: SleepScoreAnalysis
	analysisStyle: string
}[] => {
	const { sleepPattern, timeToFallAsleep, sleepDuration } = getSleepAnalysis(quizAnswers)

	const getAnalysisStyle = (analysis?: SleepScoreAnalysis): string => {
		return analysis === 'Optimal'
			? styles.sleep_snapshot_analysis_green
			: styles.sleep_snapshot_analysis_red
	}

	const rows = [
		{
			icon: 'MoonLight' as const,
			label: 'Sleep quality',
			analysis: sleepPattern,
			analysisStyle: getAnalysisStyle(sleepPattern),
		},
		{
			icon: 'ClockLight' as const,
			label: 'Time to fall asleep',
			analysis: timeToFallAsleep,
			analysisStyle: getAnalysisStyle(timeToFallAsleep),
		},
		{
			icon: 'TimeBedLight' as const,
			label: 'Sleep duration',
			className: styles.sleep_snapshot_table_icon,
			analysis: sleepDuration,
			analysisStyle: getAnalysisStyle(sleepDuration),
		},
	]

	return rows
}

const SleepSnapshotTable: React.FC<{
	quizAnswers: AllQuizAnswers
}> = ({ quizAnswers }) => {
	return (
		<div className={styles.sleep_snapshot_table_wrapper}>
			<div>
				<Type.Headline5>Your sleep snapshot</Type.Headline5>
			</div>
			<table className={styles.sleep_snapshot_table}>
				<tbody>
					{getSleepSnapshotRows(quizAnswers).map((row) => (
						<tr key={row.label}>
							<td>
								<Icon
									name={row.icon}
									className={row.className}
								/>
							</td>
							<td>{row.label}</td>
							<td className={row.analysisStyle}>{row.analysis}</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	)
}

const QuizCompleteScreen: React.FC<{
	inputStates: QuizInputStates
	handleQuizComplete: (props: { sleepPersona: string }) => void
	quizAnswers: AllQuizAnswers
}> = ({ inputStates, handleQuizComplete, quizAnswers }) => {
	const { firstName } = inputStates
	const answerIDs = Object.values(quizAnswers).map((answer) => answer.answerID)

	const persona =
		personaMappings.find((persona) => {
			const matchingAnswers = persona.answerIDs.filter((answerID) => answerIDs.includes(answerID))
			return matchingAnswers.length >= persona.minMatchNumber
		}) || catchAllPersona

	const desktopImage =
		'https://res.cloudinary.com/eightsleep/image/upload/pod-4-hub-dark-background_j3vtcv.png'

	return (
		<div className={styles.modal_wrapper_content}>
			<div className={cx(styles.left_image_wrapper, styles.complete_screen_left_image_wrapper)}>
				<Img
					src={desktopImage}
					sources={[
						{
							mediaQuery: '(min-width: 1023px)',
							src: desktopImage,
							dprHeight: 2000,
							type: 'desktop',
						},
						{
							mediaQuery: '(max-width: 1023px)',
							src: desktopImage,
							dprHeight: 2000,
							type: 'mobile',
						},
					]}
					className={cx(styles.left_image, styles.complete_screen_left_image)}
					alt="Pod 4"
					objectFit={'cover'}
					config={{
						desktop: {
							width: 1200,
							height: 2000,
						},
						mobile: {
							width: 850,
							height: 375,
						},
					}}
				/>
			</div>
			<div className={styles.quiz_question_container}>
				<div className={cx(styles.quiz_header, styles.quiz_header_splash_screen)}>
					<div></div>
					<Button.Empty
						id="sleep-quiz-complete-close"
						type="button"
						aria-label="Close modal button"
						onClick={() => handleQuizComplete({ sleepPersona: persona.name })}
						className={styles.header_button}>
						<Icon name="CloseLight" />
					</Button.Empty>
				</div>

				<div className={cx(styles.quiz_content_body, styles.quiz_content_body_sticky)}>
					<div className={styles.quiz_content_body_top}>
						<div
							className={styles.splash_screen_logo_wrapper}
							style={{ marginBottom: 0 }}>
							<Logo className={styles.header_logo} />
						</div>
						<div>
							<Type.Headline2 className={styles.headline}>
								{firstName}, {persona.personalizedTitle}
							</Type.Headline2>
							<Type.Body1 className={styles.headchapter}>{persona.explanation}</Type.Body1>
						</div>

						<SleepSnapshotTable quizAnswers={quizAnswers} />

						<div>
							<Type.Headline5 className={cx(styles.headline)}>{persona.bottomTitle}</Type.Headline5>
							<Type.Body1 className={styles.headchapter}>{persona.bottomSubtitle}</Type.Body1>
						</div>
					</div>

					<div className={styles.sticky_button_wrapper}>
						<div className={styles.blurred_background}></div>
						<Button.White
							id="sleep-quiz-complete-try-pod"
							onClick={() => handleQuizComplete({ sleepPersona: persona.name })}
							className={cx(styles.quiz_button, styles.splash_screen_button, styles.sticky_button)}>
							Learn more about the Pod
						</Button.White>
					</div>
				</div>
			</div>
		</div>
	)
}

export default QuizCompleteScreen
